/**
 * Relatórios — uma casca só para os vinte e quatro.
 *
 * O gesto é o mesmo em todos: escolhe o período, roda, olha. O que muda é o
 * endereço, os filtros e a forma da saída, e isso está descrito no `catalogo`.
 * Vinte e quatro telas quase iguais seriam vinte e quatro lugares para consertar
 * a mesma coisa.
 *
 * **Eles não rodam sozinhos.** São de 1 a 45 segundos por consulta, contra
 * tabelas de milhões de linhas — abrir um relatório não dispara nada; a pessoa
 * escolhe o período e manda rodar. É o oposto do resto do sistema, onde a lista
 * carrega ao abrir, e é de propósito.
 *
 * **Sem período a API devolve zero, calada.** Quase nenhum relatório sai daqui
 * sem data: um resultado vazio que parece "não houve movimento" é pior do que um
 * erro. As exceções estão declaradas no catálogo, sem `periodo` nos filtros — o
 * crescimento de assunto, que é série histórica inteira, e os dois de licença,
 * que são foto de agora.
 *
 * **Um deles é POST, e não grava nada.** `especialidade-licenca/por-estado` leva
 * duas listas no corpo; a trava do canário tem esse endereço na lista curta de
 * "escreve mas não grava", ao lado do login.
 */

import { useMemo, useState, useRef } from "react"
import * as Menu from "@radix-ui/react-dropdown-menu"
import { useQuery } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "@/api/cliente"
import { ErroDaApi } from "@/api/erros"
import { usarSessao } from "@/sessao/Sessao"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import {
  usarAssuntos, usarCategoriasDeVeiculo, usarMarcasDeConteudo, usarSistemasDeConteudo,
} from "@/api/catalogosDeConteudo"
import { chaveDeAcesso, GRUPOS, RELATORIOS, type Relatorio } from "./catalogo"
import { Resultado } from "./Resultado"
import { baixarCsv } from "./csv"
import { escalaFlutuante } from "@/kit/flutuante"

/** Q4: últimos 30 dias, que é o default da própria API em dois endpoints. */
function trintaDias(): { de: string; ate: string } {
  const hoje = new Date()
  const antes = new Date()
  antes.setDate(hoje.getDate() - 29)
  return { de: iso(antes), ate: iso(hoje) }
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

const ATALHOS = [
  { rotulo: "7 dias", dias: 7 },
  { rotulo: "30 dias", dias: 30 },
  { rotulo: "90 dias", dias: 90 },
] as const

export function Relatorios() {
  const { pode } = usarSessao()
  const disponiveis = RELATORIOS.filter((r) => pode(chaveDeAcesso(r.grupo)))

  const [escolhido, setEscolhido] = useState<Relatorio | null>(null)
  const [periodo, setPeriodo] = useState(trintaDias)
  const [extras, setExtras] = useState<Record<string, number | boolean | string[]>>({})
  const [rodando, setRodando] = useState<Record<string, unknown> | null>(null)

  function escolher(r: Relatorio) {
    setEscolhido(r)
    setExtras({})
    setRodando(null)
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Relatórios"
        detalhe={escolhido ? escolhido.grupo : `${disponiveis.length} disponíveis`}
      />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Lista relatorios={disponiveis} escolhido={escolhido} aoEscolher={escolher} />

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {!escolhido ? (
            <Abertura total={disponiveis.length} />
          ) : (
            <Painel
              relatorio={escolhido}
              periodo={periodo}
              aoMudarPeriodo={setPeriodo}
              extras={extras}
              aoMudarExtras={setExtras}
              resposta={rodando}
              aoResponder={setRodando}
            />
          )}
        </div>
      </div>

      <BarraDeEstado direita={escolhido?.custo
        ? `medido: ~${escolhido.custo.segundos}s, ${escolhido.custo.linhas.toLocaleString("pt-BR")} linhas num mês`
        : undefined}>
        {escolhido ? escolhido.oQueResponde : "Escolha um relatório à esquerda."}
      </BarraDeEstado>
    </div>
  )
}

// ---------------------------------------------------------------- lista

function Lista({ relatorios, escolhido, aoEscolher }: {
  relatorios: Relatorio[]
  escolhido: Relatorio | null
  aoEscolher: (r: Relatorio) => void
}) {
  return (
    <nav style={{
      flex: "0 0 250px", width: 250, overflowY: "auto", padding: 12,
      borderRight: "1px solid var(--line2)",
    }}>
      {GRUPOS.map((g) => {
        const doGrupo = relatorios.filter((r) => r.grupo === g)
        if (doGrupo.length === 0) return null
        return (
          <div key={g} style={{ marginBottom: 14 }}>
            <h3 style={{
              margin: "0 0 5px", fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
              textTransform: "uppercase", color: "var(--acao)",
            }}>
              {g}
            </h3>
            {doGrupo.map((r) => (
              <button
                key={r.chave}
                onClick={() => aoEscolher(r)}
                style={{
                  display: "block", width: "100%", textAlign: "left", marginBottom: 2,
                  border: 0, borderRadius: 5, padding: "5px 8px", cursor: "pointer",
                  fontSize: 12, lineHeight: 1.35,
                  background: escolhido?.chave === r.chave ? "var(--aba-relatorio-bg)" : "transparent",
                  color: escolhido?.chave === r.chave ? "var(--aba-relatorio-txt)" : "var(--txt)",
                  fontWeight: escolhido?.chave === r.chave ? 600 : 400,
                }}
              >
                {r.nome}
                {r.custo && r.custo.segundos >= 8 && (
                  <span title="Consulta demorada" style={{ marginLeft: 5, color: "var(--txt3)" }}>·lento</span>
                )}
              </button>
            ))}
          </div>
        )
      })}
    </nav>
  )
}

function Abertura({ total }: { total: number }) {
  return (
    <div style={{ padding: 24, maxWidth: "64ch" }}>
      <h2 style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 600 }}>
        {total} relatórios
      </h2>
      <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--txt2)", lineHeight: 1.6 }}>
        Escolha um à esquerda, ajuste o período e mande rodar. Eles não rodam ao abrir de
        propósito: cada um é uma consulta de 1 a 45 segundos contra tabelas de milhões de
        linhas — a de buscas tem 8,6 milhões de registros.
      </p>
      <p style={{ margin: 0, fontSize: 12, color: "var(--txt3)", lineHeight: 1.6 }}>
        O período começa nos últimos 30 dias. Onde a medição mostrou consulta cara ou
        resposta grande, o relatório avisa antes de você mandar rodar.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------- painel

function Painel({ relatorio, periodo, aoMudarPeriodo, extras, aoMudarExtras, resposta, aoResponder }: {
  relatorio: Relatorio
  periodo: { de: string; ate: string }
  aoMudarPeriodo: (p: { de: string; ate: string }) => void
  extras: Record<string, number | boolean | string[]>
  aoMudarExtras: (e: Record<string, number | boolean | string[]>) => void
  resposta: Record<string, unknown> | null
  aoResponder: (r: Record<string, unknown> | null) => void
}) {
  const [pedido, setPedido] = useState(0)

  const params = useMemo(() => {
    const p: Record<string, unknown> = {}
    if (relatorio.filtros.includes("periodo")) {
      p.dataInicio = periodo.de
      p.dataFim = periodo.ate
    }
    for (const [k, v] of Object.entries(extras)) {
      if (typeof v === "number" && v > 0) p[k] = v
      if (typeof v === "boolean" && v) p[k] = v
      if (Array.isArray(v) && v.length > 0) p[k] = v
    }

    // Os tres ids do crescimento sao obrigatorios na API e nao tem "todos":
    // mandar so os preenchidos faria a API recusar com "nao informados", que e
    // pior do que a tela pedir os tres.
    if (relatorio.chave === "crescimento-assunto") {
      p.categoriaVeiculoId = extras.categoriaVeiculoId ?? 0
      p.assuntoId = extras.assuntoId ?? 0
      p.sistemaId = extras.sistemaId ?? 0
    }
    // Os dois de cobertura exigem os três ids, e sem eles a API responde vazio.
    if (relatorio.chave === "veiculo-assunto-sistema" || relatorio.chave === "categoria-veiculo-assunto-sistema") {
      p.assuntoId = extras.assuntoId ?? 0
      p.sistemaId = extras.sistemaId ?? 0
      if (relatorio.chave === "veiculo-assunto-sistema") p.marcaId = extras.marcaId ?? 0
      else p.categoriaVeiculoId = extras.categoriaVeiculoId ?? 0
    }
    if (relatorio.chave === "nao-encontrado") p.conteudo = 0
    if (relatorio.chave === "arquivos-acessados" || relatorio.chave === "usuarios-acessaram") {
      p.pagina = 1
      p.pageSize = 200
    }
    return p
  }, [relatorio, periodo, extras])

  const consulta = useQuery({
    queryKey: ["relatorio", relatorio.chave, params, pedido] as const,
    // Só roda quando a pessoa manda: nenhum destes é barato o bastante para
    // disparar sozinho ao abrir.
    enabled: pedido > 0,
    // O resultado é uma foto do momento; refazer sozinho custaria segundos.
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    queryFn: async () => {
      // POST aqui nao e escrita: o corpo existe porque sao duas listas, e a
      // trava do canario tem esse endereco na lista de "escreve mas nao grava".
      const r = relatorio.metodo === "post"
        ? (await api.post<unknown>(relatorio.rota, params, foraDoProjeto())).data
        : await obter<unknown>(relatorio.rota, params)
      const normalizado = (Array.isArray(r) ? { "": r } : r) as Record<string, unknown>
      aoResponder(normalizado)
      return normalizado
    },
  })

  const dias = diasEntre(periodo.de, periodo.ate)
  const excedeu = relatorio.maximoDias !== undefined && dias > relatorio.maximoDias

  const faltamObrigatorios = useMemo(() => {
    if (relatorio.chave === "crescimento-assunto" || relatorio.chave === "categoria-veiculo-assunto-sistema") {
      return !extras.categoriaVeiculoId || !extras.assuntoId || !extras.sistemaId
    }
    if (relatorio.chave === "veiculo-assunto-sistema") {
      return !extras.marcaId || !extras.assuntoId || !extras.sistemaId
    }
    return false
  }, [relatorio.chave, extras])

  return (
    <>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--line2)" }}>
        <h2 style={{ margin: "0 0 3px", fontSize: 14, fontWeight: 600 }}>{relatorio.nome}</h2>
        <p style={{ margin: "0 0 10px", fontSize: 11.5, color: "var(--txt2)" }}>
          {relatorio.oQueResponde}
        </p>

        {relatorio.ressalva && (
          <p style={{
            margin: "0 0 10px", padding: "7px 10px", borderRadius: 5, fontSize: 11.5,
            lineHeight: 1.5, background: "var(--aviso-soft, #fdf1d8)",
            color: "var(--aviso-on-soft, #7a4b00)", maxWidth: "80ch",
          }}>
            {relatorio.ressalva}
          </p>
        )}

        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          {relatorio.filtros.includes("periodo") && (
            <>
              <Data rotulo="De" valor={periodo.de}
                    aoMudar={(v) => aoMudarPeriodo({ ...periodo, de: v })} />
              <Data rotulo="Até" valor={periodo.ate}
                    aoMudar={(v) => aoMudarPeriodo({ ...periodo, ate: v })} />
              <div style={{ display: "flex", gap: 3 }}>
                {ATALHOS.map((a) => (
                  <button
                    key={a.dias}
                    onClick={() => {
                      const ate = new Date()
                      const de = new Date()
                      de.setDate(ate.getDate() - (a.dias - 1))
                      aoMudarPeriodo({ de: iso(de), ate: iso(ate) })
                    }}
                    style={{
                      border: "1px solid var(--line)", borderRadius: 6, padding: "5px 14px",
                      fontSize: 12.5, cursor: "pointer", background: "var(--surface)", color: "var(--txt2)",
                    }}
                  >
                    {a.rotulo}
                  </button>
                ))}
                <button
                  onClick={() => {
                    const h = new Date()
                    const de = new Date(h.getFullYear(), h.getMonth() - 1, 1)
                    const ate = new Date(h.getFullYear(), h.getMonth(), 0)
                    aoMudarPeriodo({ de: iso(de), ate: iso(ate) })
                  }}
                  style={{
                    border: "1px solid var(--line)", borderRadius: 6, padding: "5px 14px",
                    fontSize: 12.5, cursor: "pointer", background: "var(--surface)", color: "var(--txt2)",
                  }}
                >
                  mês fechado
                </button>
              </div>
            </>
          )}

          <FiltrosExtras relatorio={relatorio} extras={extras} aoMudar={aoMudarExtras} />

          <button
            onClick={() => setPedido((n) => n + 1)}
            disabled={consulta.isFetching || excedeu || faltamObrigatorios}
            style={{
              border: "1px solid " + ((excedeu || faltamObrigatorios) ? "var(--line)" : "var(--acao)"),
              borderRadius: 6, padding: "5px 14px", fontSize: 12.5,
              cursor: consulta.isFetching || excedeu || faltamObrigatorios ? "default" : "pointer",
              background: (excedeu || faltamObrigatorios) ? "var(--surface2)" : "var(--acao)",
              color: (excedeu || faltamObrigatorios) ? "var(--txt3)" : "#fff",
            }}
          >
            {consulta.isFetching ? "Rodando…" : "Rodar"}
          </button>

          {resposta && !consulta.isFetching && (
            <button
              onClick={() => baixarCsv(relatorio, resposta)}
              style={{
                border: "1px solid var(--line)", borderRadius: 6, padding: "5px 14px",
                fontSize: 12.5, cursor: "pointer", background: "var(--surface)", color: "var(--txt)",
              }}
            >
              Baixar CSV
            </button>
          )}
        </div>

        {excedeu && (
          <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
            A API aceita no máximo {relatorio.maximoDias} dias, e o período escolhido tem {dias}.
          </p>
        )}

        {faltamObrigatorios && (
          <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
            Preencha todos os filtros obrigatórios para rodar este relatório.
          </p>
        )}

        {relatorio.custo && relatorio.custo.segundos >= 5 && !consulta.isFetching && (
          <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--txt3)" }}>
            Na medição esta consulta levou cerca de {relatorio.custo.segundos} segundos.
          </p>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {consulta.isFetching ? (
          <Trabalhando segundos={relatorio.custo?.segundos ?? 2} />
        ) : consulta.error ? (
          <p style={{ padding: 16, fontSize: 12.5, color: "var(--erro)", whiteSpace: "pre-wrap" }}>
            {consulta.error instanceof ErroDaApi
              ? consulta.error.message : "Não foi possível rodar o relatório."}
          </p>
        ) : resposta ? (
          <Resultado relatorio={relatorio} resposta={resposta} />
        ) : (
          <p style={{ padding: 16, fontSize: 12.5, color: "var(--txt3)" }}>
            Ajuste o período e aperte <b>Rodar</b>.
          </p>
        )}
      </div>
    </>
  )
}

function Trabalhando({ segundos }: { segundos: number }) {
  return (
    <div style={{ padding: 20 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: "var(--txt2)" }}>
        Rodando a consulta…
      </p>
      {segundos >= 5 && (
        <p style={{ margin: "5px 0 0", fontSize: 11.5, color: "var(--txt3)" }}>
          Este costuma levar uns {segundos} segundos. Não travou.
        </p>
      )}
    </div>
  )
}

function FiltrosExtras({ relatorio, extras, aoMudar }: {
  relatorio: Relatorio
  extras: Record<string, number | boolean | string[]>
  aoMudar: (e: Record<string, number | boolean | string[]>) => void
}) {
  const marcas = usarMarcasDeConteudo()
  const assuntos = usarAssuntos()
  const sistemas = usarSistemasDeConteudo()
  const categorias = usarCategoriasDeVeiculo()

  /**
   * As especialidades saem do próprio relatório irmão, o de licença sem estado.
   * Não existe catálogo: o campo é texto livre gravado no cadastro do cliente, e
   * a única lista fiel é a dos valores que de fato estão lá.
   */
  const especialidades = useQuery({
    queryKey: ["especialidades-de-licenca"] as const,
    enabled: relatorio.filtros.includes("especialidades"),
    staleTime: Infinity,
    queryFn: async () => {
      const r = await obter<{ especialidade: string }[]>(
        "api/relatorios/comercial/especialidade-licenca", {})
      const vistos = new Set<string>()
      const lista: { valor: string; rotulo: string }[] = []
      for (const x of r) {
        const v = x.especialidade ?? ""
        if (vistos.has(v)) continue
        vistos.add(v)
        // Vazio e "undefined" existem no banco e valem como recorte; o rótulo é
        // que não pode fingir que são nomes.
        lista.push({
          valor: v,
          rotulo: v === "" ? "(em branco)" : v === "undefined" ? "(undefined — lixo do cadastro)" : v,
        })
      }
      return lista.sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"))
    },
  }).data ?? []

  const def = (campo: string, v: number | boolean | string[]) => aoMudar({ ...extras, [campo]: v })

  return (
    <>
      {relatorio.filtros.includes("marca") && (
        <Escolha rotulo="Marca" itens={marcas.itens} valor={Number(extras.marcaId ?? 0)}
                 aoMudar={(v) => def("marcaId", v)} />
      )}
      {relatorio.filtros.includes("categoriaVeiculo") && (
        <Escolha rotulo="Categoria" itens={categorias.itens} valor={Number(extras.categoriaVeiculoId ?? 0)}
                 aoMudar={(v) => def("categoriaVeiculoId", v)} />
      )}
      {relatorio.filtros.includes("assunto") && (
        <Escolha rotulo="Assunto" itens={assuntos.itens} valor={Number(extras.assuntoId ?? 0)}
                 aoMudar={(v) => def("assuntoId", v)} />
      )}
      {relatorio.filtros.includes("sistema") && (
        <Escolha rotulo="Sistema" itens={sistemas.itens} valor={Number(extras.sistemaId ?? 0)}
                 aoMudar={(v) => def("sistemaId", v)} />
      )}
      {relatorio.filtros.includes("especialidades") && (
        <MultiTexto
          rotulo="Especialidades"
          ajuda="O texto que o cliente escolheu no cadastro — com as duplicatas de grafia que existem lá."
          itens={especialidades}
          escolhidos={(extras.especialidades as string[]) ?? []}
          aoMudar={(v) => def("especialidades", v)}
        />
      )}
      {relatorio.filtros.includes("estados") && (
        <MultiTexto
          rotulo="Estados"
          ajuda="A UF do endereço do cliente. Só endereços no Brasil entram na conta."
          itens={UFS.map((uf) => ({ valor: uf, rotulo: uf }))}
          escolhidos={(extras.estados as string[]) ?? []}
          aoMudar={(v) => def("estados", v)}
        />
      )}
      {relatorio.filtros.includes("somenteErros") && (
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, paddingBottom: 5 }}>
          <input type="checkbox" checked={Boolean(extras.somenteErros)}
                 onChange={(e) => def("somenteErros", e.target.checked)} />
          só erros
        </label>
      )}
    </>
  )
}

/**
 * Multi-seleção de texto.
 *
 * Existe para os dois campos que não têm id: especialidade e UF são gravadas
 * como texto, e o filtro manda a lista de textos no corpo.
 */
function MultiTexto({ rotulo, ajuda, itens, escolhidos, aoMudar }: {
  rotulo: string
  ajuda: string
  itens: { valor: string; rotulo: string }[]
  escolhidos: string[]
  aoMudar: (v: string[]) => void
}) {
  const [termo, setTermo] = useState("")
  const campo = useRef<HTMLInputElement>(null)

  const filtrados = useMemo(() => {
    const t = termo.trim().toLowerCase()
    if (!t) return itens
    return itens.filter((i) => i.rotulo.toLowerCase().includes(t) || i.valor.toLowerCase().includes(t))
  }, [itens, termo])

  const escolhidosSet = new Set(escolhidos)
  const primeiro = itens.find((i) => escolhidosSet.has(i.valor))

  const resumo =
    escolhidos.length === 0 ? "Selecionar..."
    : escolhidos.length === 1 ? (primeiro?.rotulo || "1 selecionado")
    : `${escolhidos.length} selecionados`

  function alternar(valor: string) {
    if (escolhidosSet.has(valor)) {
      aoMudar(escolhidos.filter((x) => x !== valor))
    } else {
      aoMudar([...escolhidos, valor])
    }
  }

  const ativo = escolhidos.length > 0

  return (
    <div>
      <label style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }} title={ajuda}>
        {rotulo} ({escolhidos.length})
      </label>

      <Menu.Root onOpenChange={(a) => { if (a) setTimeout(() => campo.current?.focus(), 10); else setTermo("") }}>
        <Menu.Trigger
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 32, width: 230,
            padding: "0 9px", borderRadius: 5, fontSize: 12, textAlign: "left",
            background: ativo ? "var(--brand-soft)" : "var(--surface)",
            color: ativo ? "var(--brand-on-soft)" : "var(--txt)",
            border: `1px solid ${ativo ? "var(--brand)" : "var(--line)"}`,
            fontWeight: ativo ? 600 : 400,
          }}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {resumo}
          </span>
          <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true"
               style={{ flex: "0 0 11px", fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round", strokeLinejoin: "round" }}>
            <path d="m6 9.5 6 6 6-6" />
          </svg>
        </Menu.Trigger>

        <Menu.Portal>
          <Menu.Content
            sideOffset={5} align="start"
            onCloseAutoFocus={(e) => e.preventDefault()}
            style={{
              width: 280, padding: 7, borderRadius: 10, background: "var(--surface)",
              border: "1px solid var(--line2)", boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 60,
              ...escalaFlutuante,
            }}
          >
            <input
              ref={campo}
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={`Filtrar ${rotulo.toLowerCase()}…`}
              aria-label={`Filtrar ${rotulo}`}
              style={{
                width: "100%", height: 28, padding: "0 9px", marginBottom: 6, borderRadius: 7,
                border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
              }}
            />

            <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
              {filtrados.length === 0 && <div style={{ padding: "14px 8px", fontSize: 12, color: "var(--txt3)", textAlign: "center" }}>Nada encontrado.</div>}

              {filtrados.map((i) => {
                const marcado = escolhidosSet.has(i.valor)
                return (
                  <button
                    key={i.valor}
                    onClick={() => alternar(i.valor)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, width: "100%",
                      padding: "5px 7px", borderRadius: 6, fontSize: 12.5, textAlign: "left",
                      background: marcado ? "var(--brand-soft)" : "transparent",
                      color: marcado ? "var(--brand-on-soft)" : "var(--txt)",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        flex: "0 0 14px", width: 14, height: 14, borderRadius: 4,
                        border: `1.5px solid ${marcado ? "var(--brand)" : "var(--line2)"}`,
                        background: marcado ? "var(--brand)" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {marcado && (
                        <svg width="10" height="10" viewBox="0 0 24 24"
                             style={{ fill: "none", stroke: "#FFFFFF", strokeWidth: 3.4, strokeLinecap: "round", strokeLinejoin: "round" }}>
                          <path d="m5 12.5 4.5 4.5L19 7.5" />
                        </svg>
                      )}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {i.rotulo}
                    </span>
                  </button>
                )
              })}
            </div>

            {escolhidos.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginTop: 7, paddingTop: 6,
                borderTop: "1px solid var(--line)", fontSize: 11, color: "var(--txt3)",
              }}>
                <span style={{ flex: 1 }}>{escolhidos.length} selecionados</span>
                <button onClick={() => aoMudar([])} style={{ fontSize: 11, color: "var(--brand-on-soft)", fontWeight: 600 }}>
                  limpar
                </button>
              </div>
            )}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
    </div>
  )
}

/** As 27 unidades da federação. O relatório só conta endereço com país BR. */
const UFS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT",
  "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO",
]

function Escolha({ rotulo, itens, valor, aoMudar }: {
  rotulo: string
  itens: { id: number; rotulo: string }[]
  valor: number
  aoMudar: (v: number) => void
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }}>
        {rotulo}
      </label>
      <select
        value={valor}
        onChange={(e) => aoMudar(Number(e.target.value))}
        style={{
          padding: "5px 7px", fontSize: 12, maxWidth: 210,
          border: "1px solid var(--line)", borderRadius: 5,
          background: "var(--surface)", color: "var(--txt)",
        }}
      >
        <option value={0}>Todos</option>
        {itens.slice(0, 200).map((x) => <option key={x.id} value={x.id}>{x.rotulo}</option>)}
      </select>
    </div>
  )
}

function Data({ rotulo, valor, aoMudar }: {
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }}>
        {rotulo}
      </label>
      <input
        type="date"
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        style={{
          padding: "4px 7px", fontSize: 12,
          border: "1px solid var(--line)", borderRadius: 5,
          background: "var(--surface)", color: "var(--txt)",
        }}
      />
    </div>
  )
}

function diasEntre(de: string, ate: string): number {
  const a = new Date(de).getTime()
  const b = new Date(ate).getTime()
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.round((b - a) / 86_400_000) + 1
}
